// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import PrinterFill from "@store/Library/Components/Toolkit/Icons/Printer/PrinterFill";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LinkBack, { LinkBackPresentationProps } from "@store/Library/Components/Toolkit/LinkBack";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import constants from "@store/constants";
import getOnClickHistoryBack from "@store/Framework/Common/Utilities/getOnClickHistoryBack";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { focusUpdateStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface InvoiceDetailsViewProps extends WidgetProps {}

export interface InvoiceDetailsViewStyles {
    wrapper?: InjectableCss;
    printButtonWrapper?: InjectableCss;
    printButton: ButtonPresentationProps;
    printIcon?: ButtonIconPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    block?: BlockProps;
    linkBack?: LinkBackPresentationProps;
    errorInfosBox?: InfosBoxProps;
}

export const invoiceDetailsViewStyles: InvoiceDetailsViewStyles = {
    wrapper: {
        css: css`
            table {
                vertical-align: top;
                border-collapse: collapse;
            }

            h6 {
                color: #6d6e71;
                font-weight: 700;
                line-height: 1.2;
                font-size: 16px;
            }

            img {
                display: inline-block;
                vertical-align: middle;
                max-width: 100%;
                height: auto;
            }

            && td {
                padding: 3pt;

                p {
                    margin: 0;
                }

                &.invoice-detail-line-description {
                    padding: 3pt 15pt 3pt 3pt;
                    text-align: left;
                    white-space: normal;
                }

                &.align-top {
                    vertical-align: top;
                }

                &.align-left {
                    text-align: left;
                }

                &.align-right {
                    text-align: right;
                }
            }

            .invoice-detail-wrapper {
                box-sizing: border-box;
                width: calc(8.5 * 72pt);
                min-height: calc(11 * 72pt);
                padding: calc(0.25 * 72pt);
                background-color: #fff;
                font-size: 8pt;
            }

            .invoice-detail-header,
            .invoice-detail-lines {
                width: 100%;
            }

            thead td,
            .invoice-detail-table-header td:not(.no-bg),
            .invoice-detail-table-header-cell {
                padding: 0;
                background-color: #f5f5f5;
                font-weight: 700;
            }

            .invoice-detail-header-top td {
                text-align: right;
            }

            .invoice-detail-header .invoice-detail-table-header-cell-right {
                text-align: right;
            }

            .invoice-detail-notes {
                padding: 3pt;
                border: 1px solid #999;
                margin: 12pt 0;
            }

            .invoice-detail-lines {
                border-color: #999;

                td {
                    text-align: right;
                }

                thead td {
                    text-align: left;
                }
            }

            .invoice-last-row {
                border-color: #fff;
                font-weight: 700;
            }

            .invoice-tax-detail {
                font-size: 7.5pt;
                font-weight: 400;
            }

            .invoice-detail-lines-footer-notes td {
                text-align: right;
            }

            .invoice-disclaimer {
                padding: 15pt;
                font-size: 7pt;
                font-weight: 700;
                text-align: center;

                p {
                    margin-bottom: 0;
                }
            }

            @media only screen and (max-width: calc(8.5 * 72pt)) {
                .invoice-overflow-wrapper {
                    overflow: hidden;
                    overflow-x: auto;
                    width: 95%;
                    margin: 0 auto;
                }
            }

            @media only screen {
                && {
                    min-height: 100vh;
                    padding-top: calc(0.65 * 72pt);
                    padding-bottom: 4pt;
                    background-color: #525659;
                }

                .invoice-detail-wrapper {
                    margin: 0 auto;
                }
            }
        `,
    },
    printButtonWrapper: {
        css: css`
            display: block;
            position: fixed;
            bottom: 0;
            right: ${pxToRem(75)};
            padding: ${pxToRem(16)};

            @media print {
                display: none;
            }
        `,
    },
    printButton: {
        css: css`
            display: flex;
            align-items: center;
            border-radius: 50%;
            padding: ${pxToRem(12)};
            box-shadow: rgba(0, 0, 0, 0.1) 0 1px 4px, rgba(0, 0, 0, 0.2) 0 2px 12px;

            ${ButtonIcon} {
                top: auto;
            }

            &:hover,
            &:active,
            &:focus {
                background-color: ${getColor("primary")};
                border-color: ${getColor("primary")};
                box-shadow: rgba(0, 0, 0, 0.1) 0 2px 6px, rgba(0, 0, 0, 0.2) 0 4px 16px;
                transform: scale(1.1);
                outline: none;
            }
        `,
    },
    printIcon: {
        src: PrinterFill,
        size: 35,
        css: css`
            ${focusUpdateStyles(true)}
            color: inherit;
        `,
    },
    loadingOverlay: {
        css: css`
            min-height: 100vh;
        `,
    },
    errorInfosBox: { type: "error" },
};

const styles = invoiceDetailsViewStyles;

const InvoiceDetailsView: FC<InvoiceDetailsViewProps> = () => {
    const { isLoading, value: invoice } = useContext(InvoiceStateContext);
    const backToInvoiceHistory = usePageLinkByPageType(constants.pageType.invoiceHistory);
    const backToInvoicePayment = usePageLinkByPageType(constants.pageType.invoicePayment);

    if (isLoading) {
        return <LoadingOverlay {...styles.loadingOverlay} />;
    }

    if (!invoice) {
        const backToInvoiceHistoryClick = getOnClickHistoryBack(backToInvoiceHistory);
        const backToInvoicePaymentClick = getOnClickHistoryBack(backToInvoicePayment);
        const linkBack = backToInvoicePaymentClick ? backToInvoicePayment : backToInvoiceHistory;
        const linkBackOnClick = backToInvoicePaymentClick || backToInvoiceHistoryClick;

        return (
            <Block {...styles.block}>
                <LinkBack pageLink={linkBack} onClick={linkBackOnClick} {...styles.linkBack} />
                <InfosBox {...styles.errorInfosBox}>
                    {translate("An error has occured. Please try again later.")}
                </InfosBox>
            </Block>
        );
    }

    const printClickHandler = () => {
        if (typeof window !== undefined) {
            window.print();
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <div className="invoice-overflow-wrapper">
                <div className="invoice-detail-wrapper">
                    <table className="invoice-detail-header">
                        <tbody>
                            <tr className="invoice-detail-header-top">
                                <td colSpan={10}>
                                    <h6>{translate("grimco_invoiceDetail_Invoice")}</h6>
                                    <p>
                                        {translate("grimco_invoiceDetail_Invoice")} # {invoice.invoiceNumber}
                                    </p>
                                </td>
                            </tr>
                            <tr className="invoice-detail-table-header">
                                <td colSpan={8} rowSpan={4} className="no-bg">
                                    <img src="UserFiles/Images/logo-100x100.png" alt="Grimco" />
                                </td>
                                <td>{translate("grimco_invoiceDetail_InvoiceDate")}</td>
                                <td>{translate("grimco_invoiceDetail_DueDate")}</td>
                            </tr>
                            <tr>
                                <td>
                                    <LocalizedDateTime dateTime={invoice.invoiceDate} />
                                </td>
                                <td>
                                    <LocalizedDateTime dateTime={invoice.dueDate} />
                                </td>
                            </tr>
                            <tr className="invoice-detail-table-header">
                                <td>{translate("grimco_invoiceDetail_PODate")}</td>
                                <td>{translate("grimco_invoiceDetail_PONumber")}</td>
                            </tr>
                            <tr>
                                <td>
                                    <LocalizedDateTime dateTime={invoice.poDate} />
                                </td>
                                <td>{invoice.poNumber}</td>
                            </tr>
                            <tr>
                                <td colSpan={10} className="spacer">
                                    &nbsp;
                                </td>
                            </tr>
                            <tr>
                                <td className="invoice-detail-table-header-cell">
                                    {translate("grimco_invoiceDetail_BillTo")}
                                </td>
                                <td colSpan={2}>{invoice.billTo?.companyName}</td>
                                <td className="invoice-detail-table-header-cell">
                                    {translate("grimco_invoiceDetail_CorrespondenceTo")}
                                </td>
                                <td colSpan={3}>{invoice.correspondence?.companyName}</td>
                                <td colSpan={2} className="invoice-detail-table-header-cell align-right">
                                    {translate("grimco_invoiceDetail_CustomerNumber")}
                                </td>
                                <td className="align-right">{invoice.customerNumber}</td>
                            </tr>
                            <tr>
                                <td>&nbsp;</td>
                                <td colSpan={2} className="align-top">
                                    {Boolean(invoice.billTo?.address1) && (
                                        <span>
                                            {invoice.billTo?.address1}
                                            <br />
                                        </span>
                                    )}
                                    {Boolean(invoice.billTo?.address2) && (
                                        <span>
                                            {invoice.billTo?.address2}
                                            <br />
                                        </span>
                                    )}
                                    {Boolean(invoice.billTo?.address3) && (
                                        <span>
                                            {invoice.billTo?.address3}
                                            <br />
                                        </span>
                                    )}
                                    {invoice.billTo?.city}, {invoice.billTo?.state}, {invoice.billTo?.postalCode}
                                    <br />
                                    {invoice.billTo?.country}
                                    <br />
                                </td>
                                <td>&nbsp;</td>
                                <td colSpan={2} className="align-top">
                                    {Boolean(invoice.correspondence?.address1) && (
                                        <span>
                                            {invoice.correspondence?.address1}
                                            <br />
                                        </span>
                                    )}
                                    {Boolean(invoice.correspondence?.address2) && (
                                        <span>
                                            {invoice.correspondence?.address2}
                                            <br />
                                        </span>
                                    )}
                                    {Boolean(invoice.correspondence?.address3) && (
                                        <span>
                                            {invoice.correspondence?.address3}
                                            <br />
                                        </span>
                                    )}
                                    {invoice.correspondence?.city}, {invoice.correspondence?.state},{" "}
                                    {invoice.correspondence?.postalCode}
                                    <br />
                                    {invoice.correspondence?.country}
                                    <br />
                                </td>
                                <td colSpan={4}>&nbsp;</td>
                            </tr>
                            <tr>
                                <td colSpan={10}>&nbsp;</td>
                            </tr>
                            <tr>
                                <td className="invoice-detail-table-header-cell">
                                    {translate("grimco_invoiceDetail_ShipTo")}
                                </td>
                                <td colSpan={2}>{invoice.shipTo?.companyName}</td>
                                <td className="invoice-detail-table-header-cell" colSpan={4}>
                                    {translate("grimco_invoiceDetail_Instructions")}
                                </td>
                                <td className="invoice-detail-table-header-cell" colSpan={3}>
                                    {translate("grimco_invoiceDetail_PackageID")}
                                </td>
                            </tr>
                            <tr>
                                <td rowSpan={3}>&nbsp;</td>
                                <td colSpan={2} rowSpan={3} className="align-top">
                                    {Boolean(invoice.shipTo?.address1) && (
                                        <span>
                                            {invoice.shipTo?.address1}
                                            <br />
                                        </span>
                                    )}
                                    {Boolean(invoice.shipTo?.address2) && (
                                        <span>
                                            {invoice.shipTo?.address2}
                                            <br />
                                        </span>
                                    )}
                                    {Boolean(invoice.shipTo?.address3) && (
                                        <span>
                                            {invoice.shipTo?.address3}
                                            <br />
                                        </span>
                                    )}
                                    {invoice.shipTo?.city}, {invoice.shipTo?.state}, {invoice.shipTo?.postalCode}
                                    <br />
                                    {invoice.shipTo?.country}
                                    <br />
                                </td>
                                <td colSpan={4}>{invoice.instructions || "\u00A0"}</td>
                                <td colSpan={3}>{invoice.packageID || "\u00A0"}</td>
                            </tr>
                            <tr className="invoice-detail-table-header">
                                <td colSpan={2}>{translate("grimco_invoiceDetail_ShipPoint")}</td>
                                <td colSpan={3}>{translate("grimco_invoiceDetail_Via")}</td>
                                <td>{translate("grimco_invoiceDetail_Shipped")}</td>
                                <td>{translate("grimco_invoiceDetail_Terms")}</td>
                            </tr>
                            <tr>
                                <td colSpan={2}>{invoice.shipPoint}</td>
                                <td colSpan={3}>{invoice.shipVia}</td>
                                <td>
                                    <LocalizedDateTime dateTime={invoice.shipDate} />
                                </td>
                                <td>{invoice.terms}</td>
                            </tr>
                            <tr>
                                <td colSpan={10} className="spacer">
                                    &nbsp;
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {Boolean(invoice.notes) && (
                        <div className="invoice-detail-notes">
                            <strong>{translate("{0}:", translate("Notes"))}</strong>
                            <p>{invoice.notes}</p>
                        </div>
                    )}
                    <table {...{ border: 1 }} className="invoice-detail-lines">
                        <thead>
                            <tr>
                                <td>{translate("grimco_invoiceDetail_Line")}</td>
                                <td>{translate("grimco_invoiceDetail_ProductAndDescription")}</td>
                                <td>{translate("grimco_invoiceDetail_OrderQuantity")}</td>
                                <td>{translate("grimco_invoiceDetail_BackorderQuantity")}</td>
                                <td>{translate("grimco_invoiceDetail_ShippedQuantity")}</td>
                                <td>{translate("grimco_invoiceDetail_QtyUM")}</td>
                                <td>{translate("grimco_invoiceDetail_UnitPrice")}</td>
                                <td>{translate("grimco_invoiceDetail_PriceUM")}</td>
                                <td>{translate("grimco_invoiceDetail_DiscountMultiplier")}</td>
                                <td>{translate("grimco_invoiceDetail_Amount")} (Net)</td>
                            </tr>
                        </thead>
                        <tbody>
                            {invoice.invoiceLines?.map(line => {
                                return (
                                    <tr key={line.id}>
                                        <td className="invoice-detail-line-line">{line.lineNumber}</td>
                                        <td className="invoice-detail-line-description">
                                            <p className="invoice-detail-line-sku">{line.productNumber}</p>
                                            <p>{line.productDescription}</p>
                                        </td>
                                        <td className="invoice-detail-line-order-qty">{line.orderedQuantity}</td>
                                        <td className="invoice-detail-line-bo-qty">{line.backOrderQuantity}</td>
                                        <td className="invoice-detail-line-shipped-qty">{line.shippedQuantity}</td>
                                        <td className="invoice-detail-line-qty-uom">{line.unitOfMeasure}</td>
                                        <td className="invoice-detail-line-unit-price">{line.unitPriceDisplay}</td>
                                        <td className="invoice-detail-line-price-uom">{line.unitOfMeasure}</td>
                                        <td className="invoice-detail-line-discount">{line.discountAmountDisplay}</td>
                                        <td className="invoice-detail-line-net-price">{line.lineTotalDisplay}</td>
                                    </tr>
                                );
                            })}
                            <tr className="invoice-last-row">
                                <td className="align-top">{invoice.lines?.length || 0}</td>
                                <td className="align-top align-left">{translate("grimco_invoiceDetail_LinesTotal")}</td>
                                <td colSpan={2} className="align-top">
                                    {translate("grimco_invoiceDetail_QtyShippedTotal")}
                                </td>
                                <td className="align-top">{invoice.quantityShippedTotal}</td>
                                <td colSpan={2}></td>
                                <td colSpan={2}>{translate("grimco_invoiceDetail_Subtotal")}</td>
                                <td>{invoice.productTotalDisplay}</td>
                            </tr>
                            {Boolean(invoice.orderDiscountAmount && invoice.orderDiscountAmount > 0) && (
                                <tr className="invoice-last-row">
                                    <td colSpan={9}>{translate("grimco_invoiceDetail_OrderDiscount")}</td>
                                    <td>({invoice.orderDiscountAmount})</td>
                                </tr>
                            )}
                            {invoice.customOtherCharges?.map(charge => {
                                return (
                                    <tr key={charge.description} className="invoice-last-row">
                                        <td colSpan={9}>{charge.description}</td>
                                        <td>{charge.amountDisplay}</td>
                                    </tr>
                                );
                            })}
                            <tr className="invoice-last-row">
                                <td colSpan={9}>{translate("grimco_invoiceDetail_Taxes")}</td>
                                <td>{invoice.taxAmountDisplay}</td>
                            </tr>
                            {Boolean(invoice.countryTaxAmount && invoice.countryTaxAmount > 0) && (
                                <tr className="invoice-last-row invoice-tax-detail">
                                    <td colSpan={9}>{translate("grimco_invoiceDetail_GST")}</td>
                                    <td>{invoice.countryTaxAmountDisplay}</td>
                                </tr>
                            )}
                            {Boolean(invoice.provincialTaxAmount && invoice.provincialTaxAmount > 0) && (
                                <tr className="invoice-last-row invoice-tax-detail">
                                    <td colSpan={9}>{translate("grimco_invoiceDetail_PST")}</td>
                                    <td>{invoice.provincialTaxAmountDisplay}</td>
                                </tr>
                            )}
                            <tr className="invoice-last-row">
                                <td colSpan={9}>{translate("grimco_invoiceDetail_Total")}</td>
                                <td>{invoice.invoiceTotalDisplay}</td>
                            </tr>
                            <tr className="invoice-detail-lines-footer-notes invoice-last-row">
                                <td colSpan={10}>
                                    <p>
                                        {translate("grimco_invoiceDetail_CashDiscount").replace(
                                            "%1%",
                                            invoice.discountAmountDisplay,
                                        )}
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="invoice-disclaimer">
                        <p>*{siteMessage("grimco_invoiceDetail_disclaimer")}*</p>
                        <p>{siteMessage("grimco_invoiceDetail_terms")}</p>
                        <p>
                            {siteMessage("grimco_invoiceDetail_taxID")}: : GST / HST #: 85480 7799 RT0001 QST #:
                            1213223808 PST BC #: 1014-0338
                        </p>
                        <p>{siteMessage("grimco_invoiceDetail_contact")}</p>
                        <p>{siteMessage("grimco_invoiceDetail_online")}</p>
                    </div>
                </div>
            </div>
            <StyledWrapper {...styles.printButtonWrapper}>
                <Button {...styles.printButton} onClick={printClickHandler}>
                    <ButtonIcon {...styles.printIcon} />
                    <VisuallyHidden>{translate("Print")}</VisuallyHidden>
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailsView,
    definition: {
        group: "Invoice Details",
        displayName: "View",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
